import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { SocketProvider } from './contexts/SocketContext'
import { TourProvider } from './contexts/TourContext'
import { Toaster } from 'react-hot-toast'
// ProfileCompletionModal replaced by ProfileCompletionBanner in dashboard

// Layouts
import MainLayout from './layouts/MainLayout'

// Pages
import NewHomePage from './pages/NewHomePage'
import DarkLoginPage from './pages/DarkLoginPage'
import DarkRegisterPage from './pages/DarkRegisterPage'
import RegisterPage from './pages/RegisterPage'
import DarkDashboardPage from './pages/DarkDashboardPage'
import ProjectsPage from './pages/ProjectsPage'
import ProjectDetailPage from './pages/ProjectDetailPage'
import ProjectProposalsPage from './pages/ProjectProposalsPage'
import CreatorsPage from './pages/CreatorsPage'
import OpdrachtStartPage from './pages/OpdrachtStartPage'
import ProfilePage from './pages/ProfilePage'
import ProjectCreatePage from './pages/ProjectCreatePage'
import MyProjectsPage from './pages/MyProjectsPage'
import AnalyticsPage from './pages/AnalyticsPage'
import EarningsPage from './pages/EarningsPage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import CreatorDetailPage from './pages/CreatorDetailPage'
import ProjectEditPage from './pages/ProjectEditPage'
import MessagesPage from './pages/MessagesPage'
import MyActiveProjectsPage from './pages/MyActiveProjectsPage'
import MyProposalsPage from './pages/MyProposalsPage'
import MyInvitationsPage from './pages/MyInvitationsPage'
import ProjectWorkspacePage from './pages/ProjectWorkspacePage'
import ProjectPaymentPage from './pages/ProjectPaymentPage'
import ApprovePaymentPage from './pages/ApprovePaymentPage'
import PaymentSuccessPage from './pages/PaymentSuccessPage'
import PackageCheckoutPage from './pages/PackageCheckoutPage'
import PackagesMarketplacePage from './pages/PackagesMarketplacePage'
import PaymentCancelPage from './pages/PaymentCancelPage'
import OAuthSuccessPage from './pages/OAuthSuccessPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
// Admin pages lazy loaded to keep them out of the main bundle
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'))
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'))
const AdminAutomationsPage = React.lazy(() => import('./pages/AdminAutomationsPage'))
import CompaniesPage from './pages/CompaniesPage'
import CompanyDetailPage from './pages/CompanyDetailPage'
import CreatorScriptPage from './pages/CreatorScriptPage'

// Loading component
const Loading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
  </div>
)

// Protected Route component
const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading } = useAuth()
  
  if (loading) return <Loading />
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

// Public Route component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth()
  
  if (loading) return <Loading />
  
  if (user) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

// Profile completion is now handled by ProfileCompletionBanner in dashboard

// mobiel 2026-09-10 (audit App.jsx:96-105, patroon 16; publieke audit rij #38): toasts staan top-center
// onder de sticky headers (top 80) in plaats van top-right over hamburger, bel, berichten en terugknoppen
// heen; huisstijl wit met border i.p.v. donker. Onder 1024 px bovendien nooit breder dan het scherm.
const ResponsiveToaster = () => {
  const query = '(max-width: 1023px)'
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  )
  React.useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = (e) => setIsMobile(e.matches)
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])

  return (
    <Toaster
      position="top-center"
      containerStyle={isMobile ? { top: 80, left: 12, right: 12 } : { top: 80 }}
      toastOptions={{
        duration: 4000,
        style: {
          background: '#ffffff',
          color: '#0F172A',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
          ...(isMobile ? { maxWidth: 'calc(100vw - 24px)' } : {}),
        },
        success: { iconTheme: { primary: '#3EB489', secondary: '#ffffff' } },
      }}
    />
  )
}

function App() {
  return (
    <SocketProvider>
      <TourProvider>
        <ResponsiveToaster />
        <Routes>
      {/* New Dark Theme Pages - standalone */}
      <Route index element={<NewHomePage />} />
      <Route path="login" element={
        <PublicRoute>
          <DarkLoginPage />
        </PublicRoute>
      } />
      <Route path="register" element={
        <PublicRoute>
          <DarkRegisterPage />
        </PublicRoute>
      } />
      <Route path="forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      
      {/* Protected Dashboard Route - standalone dark theme */}
      <Route path="dashboard" element={
        <ProtectedRoute>
          <DarkDashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Standalone dark theme pages */}
      <Route path="projects" element={<ProjectsPage />} />
      {/* Moet vóór projects/:id staan: anders belandt /projects/my op de
          detailpagina met id="my" en klapt de proposals-call om. */}
      <Route path="projects/my" element={<Navigate to={`/my-projects${window.location.search}`} replace />} />
      <Route path="projects/:id" element={<ProjectDetailPage />} />
      <Route path="projects/:id/proposals" element={<ProjectProposalsPage />} />
      {/* Publiek sinds 2026-07-15: marktplaats zichtbaar zonder account,
          gate zit op contact/boeken (handleStartChat → /login) */}
      <Route path="creators" element={<CreatorsPage />} />
      <Route path="opdracht-starten" element={<OpdrachtStartPage />} />
      <Route path="creators/:id" element={<CreatorDetailPage />} />
      <Route path="companies" element={<CompaniesPage />} />
      <Route path="companies/:id" element={<CompanyDetailPage />} />
      <Route path="profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="projects/create" element={
        <ProtectedRoute roles={['bedrijf', 'agency']}>
          <ProjectCreatePage />
        </ProtectedRoute>
      } />
      <Route path="projects/:id/edit" element={
        <ProtectedRoute roles={['bedrijf', 'agency']}>
          <ProjectEditPage />
        </ProtectedRoute>
      } />
      <Route path="my-projects" element={
        <ProtectedRoute roles={['bedrijf', 'agency']}>
          <MyProjectsPage />
        </ProtectedRoute>
      } />
      <Route path="analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="earnings" element={
        <ProtectedRoute roles={['creator']}>
          <EarningsPage />
        </ProtectedRoute>
      } />
      <Route path="messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="my-active-projects" element={
        <ProtectedRoute roles={['creator']}>
          <MyActiveProjectsPage />
        </ProtectedRoute>
      } />
      <Route path="my-proposals" element={
        <ProtectedRoute roles={['creator']}>
          <MyProposalsPage />
        </ProtectedRoute>
      } />
      <Route path="my-invitations" element={
        <ProtectedRoute roles={['creator']}>
          <MyInvitationsPage />
        </ProtectedRoute>
      } />
      <Route path="project-workspace/:projectId" element={
        <ProtectedRoute>
          <DarkDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="projects/:projectId/payment" element={
        <ProtectedRoute roles={['bedrijf', 'agency']}>
          <ProjectPaymentPage />
        </ProtectedRoute>
      } />
      <Route path="project/:projectId/approve-payment" element={
        <ProtectedRoute roles={['bedrijf', 'agency']}>
          <ApprovePaymentPage />
        </ProtectedRoute>
      } />
      
      {/* Pakketten marktplaats (publiek) */}
      <Route path="pakketten" element={<PackagesMarketplacePage />} />

      {/* Package Checkout */}
      <Route path="checkout/:creatorId/:packageIndex" element={
        <ProtectedRoute roles={['bedrijf', 'agency']}>
          <PackageCheckoutPage />
        </ProtectedRoute>
      } />

      {/* Payment Result Pages */}
      <Route path="payment-success" element={<PaymentSuccessPage />} />
      <Route path="payment-cancel" element={<PaymentCancelPage />} />

      {/* OAuth Success Page */}
      <Route path="oauth-success" element={<OAuthSuccessPage />} />

      {/* Admin Routes - lazy loaded */}
      <Route path="admin/login" element={<React.Suspense fallback={<Loading />}><AdminLogin /></React.Suspense>} />
      <Route path="admin/automations" element={<React.Suspense fallback={<Loading />}><AdminAutomationsPage /></React.Suspense>} />
      <Route path="admin" element={<React.Suspense fallback={<Loading />}><AdminDashboard /></React.Suspense>} />

      {/* Terms and Privacy Pages */}
      <Route path="algemene-voorwaarden" element={<TermsPage />} />
      <Route path="privacy-policy" element={<PrivacyPage />} />
      <Route path="creator-script" element={<CreatorScriptPage />} />
      
      {/* Other routes with MainLayout */}
      <Route path="/" element={<MainLayout />}>
      </Route>
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </TourProvider>
    </SocketProvider>
  )
}

export default App